import { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { PageHeader, Loader, EmptyState } from "@/components/common";
import { timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ChefHat, Leaf, Drumstick, Flame, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Kitchen() {
  const [tickets, setTickets] = useState(null);

  const load = useCallback(() => api.get("/restaurant/kot").then((r) => setTickets(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const advance = async (oid, to) => {
    await api.post(`/restaurant/orders/${oid}/kot`, { to });
    toast.success(to === "preparing" ? "Started cooking 🔥" : "Marked ready ✅");
    load();
  };

  if (!tickets) return <Loader />;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="kitchen-page">
      <PageHeader title="Kitchen (KOT)" subtitle={`${tickets.length} active ticket${tickets.length !== 1 ? "s" : ""} · live board`} />
      {tickets.length === 0 ? (
        <EmptyState title="All caught up 🎉" description="No pending kitchen tickets right now." icon={ChefHat} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {tickets.map((t) => {
            const hasNew = t.items.some((i) => i.kot_status === "new");
            return (
              <div key={t.order_id} data-testid={`kot-${t.order_id}`} className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-secondary/60 border-b border-border">
                  <div className="font-heading font-bold">{t.table_name ? `Table ${t.table_name}` : "Takeaway"}</div>
                  <div className="text-[11px] text-muted-foreground">{timeAgo(t.created_at)}</div>
                </div>
                <div className="p-3 space-y-1.5">
                  {t.items.map((it, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {it.veg ? <Leaf className="h-3.5 w-3.5 text-[hsl(var(--success))]" /> : <Drumstick className="h-3.5 w-3.5 text-destructive" />}
                      <span className="font-semibold">{it.quantity}×</span>
                      <span className="truncate flex-1">{it.name}</span>
                      {it.kot_status === "preparing" && <Flame className="h-3.5 w-3.5 text-[hsl(var(--warning))]" />}
                    </div>
                  ))}
                </div>
                <div className="p-3 pt-0">
                  {hasNew ? (
                    <Button size="sm" onClick={() => advance(t.order_id, "preparing")} data-testid={`start-${t.order_id}`} className="w-full font-semibold"><Flame className="h-4 w-4 mr-1" /> Start Cooking</Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => advance(t.order_id, "ready")} data-testid={`ready-${t.order_id}`} className="w-full font-semibold"><CheckCircle2 className="h-4 w-4 mr-1" /> Mark Ready</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
